import { useState, useEffect } from "react";
import { Check, Sparkles, Zap, Shield, Clock, Bell, Heart, BarChart3, Loader2, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { useNavigate, useSearchParams, Navigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { PRO_PLANS, ProPlan, useProStatus } from "@/hooks/useProStatus";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Helmet } from "react-helmet-async";
import { isNativeCapacitorApp } from "@/lib/runtime";
import { AppStoreCTA } from "@/components/AppStoreCTA";

const FREE_FEATURES = [
  { icon: Clock, label: "Real-time TSA wait times" },
  { icon: Zap, label: "Flight status board" },
  { icon: Heart, label: "Restaurant browsing" },
  { icon: Sparkles, label: "5 AI chat messages/day" },
];

const PRO_FEATURES = [
  { icon: Sparkles, label: "Unlimited AI airport assistant" },
  { icon: Bell, label: "Push notification alerts" },
  { icon: Clock, label: "Leave-By countdown timer" },
  { icon: Heart, label: "Unlimited saved airports" },
  { icon: BarChart3, label: "Security line forecast" },
  { icon: BarChart3, label: "Hourly wait time charts" },
  { icon: Shield, label: "Priority support" },
];

const Pricing = () => {
  const [selectedPlan, setSelectedPlan] = useState<ProPlan["id"]>("annual");
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, session } = useAuth();
  const { isPro, proTier, subscriptionEnd, refreshProStatus } = useProStatus();

  // Hard guard: never let the iOS app land on the Stripe-based pricing page.
  // Apple Guideline 3.1.1 requires IAP for digital goods, and window.open()
  // is a no-op in WKWebView so the Stripe buttons would just spin forever.
  if (isNativeCapacitorApp()) {
    return <Navigate to="/ios/pricing" replace />;
  }

  // Handle return from Stripe
  useEffect(() => {
    if (searchParams.get("success") === "true") {
      toast({ title: "Welcome to Pro! 🎉", description: "Your upgrade is active. Enjoy your premium features!" });
      refreshProStatus();
    } else if (searchParams.get("canceled") === "true") {
      toast({ variant: "destructive", title: "Checkout canceled", description: "No charges were made." });
    }
  }, [searchParams, toast, refreshProStatus]);

  const handleUpgrade = async (planId: ProPlan["id"]) => {
    if (!user || !session) {
      toast({ title: "Sign in required", description: "Please sign in to upgrade to Pro." });
      navigate("/auth");
      return;
    }

    const plan = PRO_PLANS.find((p) => p.id === planId);
    if (!plan) return;

    setCheckoutLoading(planId);
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { priceId: plan.priceId },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (error) throw error;
      if (data?.url) {
        window.open(data.url, "_blank");
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (err) {
      console.error("Checkout error:", err);
      toast({ variant: "destructive", title: "Checkout failed", description: "Please try again or contact support." });
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handleManageSubscription = async () => {
    if (!session) return;
    setPortalLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("customer-portal", {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (error) throw error;
      if (data?.url) {
        window.open(data.url, "_blank");
      }
    } catch (err) {
      console.error("Portal error:", err);
      toast({ variant: "destructive", title: "Error", description: "Could not open subscription management. Please try again." });
    } finally {
      setPortalLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-secondary/20 to-primary/5 pb-20">
      <Helmet>
        <title>Airport Buddy Pro — Upgrade Your Travel Experience</title>
        <meta name="description" content="Unlock unlimited AI chat, push alerts, Leave-By timer, and more with Airport Buddy Pro." />
        <link rel="canonical" href="https://airportbuddy.app/pricing" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Airport Buddy Pro — Pricing" />
        <meta property="og:description" content="Upgrade to Pro for unlimited AI chat, push alerts, and Leave-By timer." />
        <meta property="og:url" content="https://airportbuddy.app/pricing" />
      </Helmet>

      <div className="container max-w-4xl mx-auto px-4 py-8 md:py-16">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-6 gap-1">
          ← Back
        </Button>

        {/* Active Pro Banner */}
        {isPro && (
          <div className="mb-8 p-4 rounded-xl border-2 border-primary bg-primary/5 text-center space-y-2">
            <div className="flex items-center justify-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              <span className="font-bold text-foreground">You're on Pro{proTier ? ` (${proTier})` : ""}!</span>
            </div>
            {subscriptionEnd && (
              <p className="text-sm text-muted-foreground">
                Renews {new Date(subscriptionEnd).toLocaleDateString()}
              </p>
            )}
            {proTier === "lifetime" && (
              <p className="text-sm text-muted-foreground">Lifetime access — never expires</p>
            )}
            <Button variant="outline" size="sm" onClick={handleManageSubscription} disabled={portalLoading} className="gap-1">
              {portalLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
              Manage Subscription
            </Button>
          </div>
        )}

        {/* Hero */}
        <div className="text-center mb-10 space-y-3">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-2">
            <Sparkles className="h-4 w-4" />
            Upgrade to Pro
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-foreground">Never miss a flight again</h1>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto">
            Get real-time alerts, AI-powered travel assistance, and tools that save you hours at the airport.
          </p>
        </div>

        {!isNativeCapacitorApp() && (
          <div className="mb-10 max-w-2xl mx-auto">
            <AppStoreCTA
              placement="pricing_page"
              headline="Pro works best in the iPhone app"
              subline="Push alerts for security lines and gate changes only arrive on the native app. Download it free, then upgrade."
            />
          </div>
        )}

        {/* Plans */}
        <div className="grid md:grid-cols-3 gap-4 mb-12">
          {PRO_PLANS.map((plan) => {
            const isCurrentPlan = isPro && proTier === plan.id;
            const isLoading = checkoutLoading === plan.id;

            return (
              <Card
                key={plan.id}
                className={cn(
                  "relative p-5 cursor-pointer transition-all duration-200 border-2",
                  isCurrentPlan
                    ? "border-primary/50 bg-primary/5"
                    : selectedPlan === plan.id
                    ? "border-primary shadow-lg shadow-primary/10 scale-[1.02]"
                    : "border-border hover:border-primary/30"
                )}
                onClick={() => !isPro && setSelectedPlan(plan.id)}
              >
                {plan.badge && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full bg-primary text-primary-foreground text-xs font-bold whitespace-nowrap">
                    {plan.badge}
                  </span>
                )}
                {isCurrentPlan && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full bg-primary text-primary-foreground text-xs font-bold whitespace-nowrap">
                    ✓ Your Plan
                  </span>
                )}
                <div className="text-center space-y-2 pt-1">
                  <h3 className="font-semibold text-foreground">{plan.name}</h3>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-3xl font-bold text-foreground">{plan.price}</span>
                    <span className="text-sm text-muted-foreground">{plan.period}</span>
                  </div>
                  {plan.savings && (
                    <p className="text-xs font-medium text-primary">{plan.savings}</p>
                  )}
                  <Button
                    className="w-full mt-3 min-h-[44px]"
                    variant={isCurrentPlan ? "outline" : selectedPlan === plan.id ? "default" : "outline"}
                    disabled={isCurrentPlan || isLoading}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!isCurrentPlan) handleUpgrade(plan.id);
                    }}
                  >
                    {isLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : isCurrentPlan ? (
                      "Current Plan"
                    ) : plan.id === "lifetime" ? (
                      "Claim Lifetime Deal"
                    ) : (
                      "Start Pro"
                    )}
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Feature comparison */}
        <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">Free</h3>
            <div className="space-y-3">
              {FREE_FEATURES.map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-center gap-3 text-sm text-foreground">
                  <div className="w-7 h-7 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0">
                    <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                  </div>
                  {label}
                </div>
              ))}
            </div>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-primary uppercase tracking-wider mb-4 flex items-center gap-1">
              <Sparkles className="h-3.5 w-3.5" /> Pro — Everything in Free, plus:
            </h3>
            <div className="space-y-3">
              {PRO_FEATURES.map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-center gap-3 text-sm text-foreground">
                  <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Icon className="h-3.5 w-3.5 text-primary" />
                  </div>
                  {label}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="mt-16 max-w-xl mx-auto space-y-6">
          <h2 className="text-xl font-bold text-foreground text-center">Frequently Asked Questions</h2>
          <div className="space-y-4">
            <div>
              <p className="font-medium text-foreground text-sm">Can I cancel anytime?</p>
              <p className="text-sm text-muted-foreground mt-1">Yes! Monthly and annual plans can be cancelled anytime. You'll keep access until the end of your billing period.</p>
            </div>
            <div>
              <p className="font-medium text-foreground text-sm">What's the Founder Lifetime deal?</p>
              <p className="text-sm text-muted-foreground mt-1">A one-time payment of $39.99 for permanent Pro access. Limited to the first 160 users at launch — once they're gone, they're gone.</p>
            </div>
            <div>
              <p className="font-medium text-foreground text-sm">Do I need Pro to check wait times?</p>
              <p className="text-sm text-muted-foreground mt-1">No! Real-time TSA wait times, flight status, and restaurant browsing are always free. Pro unlocks advanced tools like the AI assistant and alerts.</p>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-center gap-3 text-xs text-muted-foreground mt-8">
          <a href="/terms" className="underline hover:text-foreground transition-colors">Terms of Use</a>
          <span>•</span>
          <a href="/privacy" className="underline hover:text-foreground transition-colors">Privacy Policy</a>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
